import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { type TenantConfig } from "@waypoint/shared";
import {
  initI18n,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from "@/i18n";
import { resolvedTenant, resolvedTenantId } from "@/lib/resolveTenant";
import { applyTenantTheme } from "@/lib/tenantTheme";

const TENANT_ID = resolvedTenantId;
const tenantConfig: TenantConfig | undefined = resolvedTenant;

// Apply CSS custom properties synchronously at module load so the first paint
// is already themed (no flash of default colors before the effect runs).
if (tenantConfig) applyTenantTheme(tenantConfig);

// Initialize i18n with tenant
const i18nInstance = initI18n(TENANT_ID, tenantConfig?.translations);

interface TenantContextValue {
  tenant: TenantConfig;
  tenantId: string;
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  supportedLanguages: readonly SupportedLanguage[];
}

const TenantContext = createContext<TenantContextValue | null>(null);

export const useTenant = (): TenantContextValue => {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error("useTenant must be used within TenantProvider");
  }
  return ctx;
};

export const useTenantOrNull = (): TenantContextValue | null =>
  useContext(TenantContext);

export const TenantProvider = ({ children }: { children: ReactNode }) => {
  // Track current language
  const [language, setLanguageState] = useState<SupportedLanguage>(() => {
    const stored = localStorage.getItem("i18nextLng");
    if (stored && SUPPORTED_LANGUAGES.includes(stored as SupportedLanguage)) {
      return stored as SupportedLanguage;
    }
    return "en";
  });

  const setLanguage = (lang: SupportedLanguage) => {
    i18nInstance.changeLanguage(lang);
    setLanguageState(lang);
    localStorage.setItem("i18nextLng", lang);
  };

  // Set data-tenant attribute for CSS theming
  useEffect(() => {
    document.documentElement.setAttribute("data-tenant", TENANT_ID);
    document.documentElement.setAttribute("lang", language);

    // Update document title and meta
    if (tenantConfig) {
      applyTenantTheme(tenantConfig);
      document.title = tenantConfig.branding?.siteTitle || "Travel";

      // Update theme-color meta tag
      const themeColorMeta = document.querySelector('meta[name="theme-color"]');
      if (themeColorMeta && tenantConfig.colors?.primary) {
        themeColorMeta.setAttribute("content", tenantConfig.colors.primary);
      }
    }
  }, [language]);

  if (!tenantConfig) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-red-500">
          Configuration error: Tenant "{TENANT_ID}" not found
        </p>
      </div>
    );
  }

  return (
    <TenantContext.Provider
      value={{
        tenant: tenantConfig,
        tenantId: TENANT_ID,
        language,
        setLanguage,
        supportedLanguages: SUPPORTED_LANGUAGES,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};
